import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Button,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Divider,
  Link,
} from "@material-ui/core";

import { useConsent } from "../consent/ConsentContext";

/* ---------------------------------------------------
   STYLES
--------------------------------------------------- */

const useStyles = makeStyles((theme) => ({
  banner: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 2000,
    padding: theme.spacing(2),
    background: "#111827", // darker neutral
    color: "#f9fafb",
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    boxShadow: "0 -4px 20px rgba(0,0,0,0.35)",
  },

  text: {
    flex: 1,
    minWidth: 260,
  },

  actions: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },

  button: {
    textTransform: "none",
    fontWeight: 600,
    minWidth: 120,
  },

  /* Accept */
  accept: {
    background: "#22c55e",
    color: "#fff",
    "&:hover": {
      background: "#16a34a",
    },
  },

  /* Reject */
  reject: {
    background: "#ef4444",
    color: "#fff",
    "&:hover": {
      background: "#dc2626",
    },
  },

  /* Preferences — FIXED CONTRAST */
  prefs: {
    background: "#2563eb",
    color: "#fff",
    "&:hover": {
      background: "#1d4ed8",
    },
  },

  policy: {
    display: "block",
    marginTop: theme.spacing(1),
    color: "#93c5fd",
    fontSize: "0.85rem",
  },
}));

/* ---------------------------------------------------
   COMPONENT
--------------------------------------------------- */

export default function CookieBanner() {
  const classes = useStyles();

  const {
    isBannerVisible,
    acceptAll,
    rejectAll,
    savePreferences,
    consent,
  } = useConsent();

  const [openPrefs, setOpenPrefs] = useState(false);

  const [prefs, setPrefs] = useState({
    analytics: consent.analytics,
    marketing: consent.marketing,
    preferences: consent.preferences,
  });

  if (!isBannerVisible) return null;

  /* -----------------------------
     HANDLERS
  ------------------------------ */

  const handleToggle = (key) => (e) => {
    setPrefs({
      ...prefs,
      [key]: e.target.checked,
    });
  };

  const handleSavePrefs = () => {
    savePreferences(prefs);
    setOpenPrefs(false);
  };

  /* -----------------------------
     RENDER
  ------------------------------ */

  return (
    <>
      {/* Banner */}
      <Paper elevation={6} className={classes.banner}>
        <div className={classes.text}>
          <Typography variant="body1">
            We use cookies to enhance your experience, analyze traffic, and
            personalize content. You can accept all cookies or manage your
            preferences.
          </Typography>

          {/* Optional policy link */}
          <Link
            href="/auth/privacy-policy"
            target="_blank"
            rel="noopener"
            className={classes.policy}
          >
            View Privacy Policy
          </Link>
        </div>

        <div className={classes.actions}>
          <Button
            className={`${classes.button} ${classes.reject}`}
            onClick={rejectAll}
          >
            Reject
          </Button>

          <Button
            className={`${classes.button} ${classes.prefs}`}
            onClick={() => setOpenPrefs(true)}
          >
            Preferences
          </Button>

          <Button
            className={`${classes.button} ${classes.accept}`}
            onClick={acceptAll}
          >
            Accept All
          </Button>
        </div>
      </Paper>

      {/* Preferences Modal */}
      <Dialog
        open={openPrefs}
        onClose={() => setOpenPrefs(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Cookie Preferences</DialogTitle>

        <DialogContent dividers>
          <FormControlLabel
            control={<Switch checked disabled />}
            label="Necessary"
          />

          <Divider style={{ margin: "12px 0" }} />

          <FormControlLabel
            control={
              <Switch
                checked={prefs.analytics}
                onChange={handleToggle("analytics")}
              />
            }
            label="Analytics Cookies"
          />

          <FormControlLabel
            control={
              <Switch
                checked={prefs.preferences}
                onChange={handleToggle("preferences")}
              />
            }
            label="Preferences Cookies"
          />

          <FormControlLabel
            control={
              <Switch
                checked={prefs.marketing}
                onChange={handleToggle("marketing")}
              />
            }
            label="Marketing Cookies"
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenPrefs(false)}>Cancel</Button>

          <Button color="primary" variant="contained" onClick={handleSavePrefs}>
            Save Preferences
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
